import {
  getDisplayErrorCode,
  isUnknownError,
  removeRawMessageSuffix,
} from "@/utils/error/error-display-message";
import { useTranslation } from "@/utils/i18n/client";
import { SATError, SATErrorType } from "@sadtx/sat-error";
import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { NavigateFunction, useLocation, useNavigate } from "react-router";
import BottomButtonSection from "../buttom-button-section/buttom-bottom-section";
import Loading from "../loading/loading";
import styles from "./modal-popup.module.css";

export interface ModalButton {
  text: string;
  onClick: () => void;
  variant?: "primary" | "secondary";
}

export interface ModalButtonProps {
  title: string;
  onClick: () => void;
}

export interface ModalPopupProps {
  isOpen: boolean;
  error: SATError | null;
  title: string;
  description: string[];
  detail?: string;
  okButton: ModalButtonProps;
  cancelButton?: ModalButtonProps;
  onClose: () => void;
}

interface ModalContextType {
  showModal: (props: Omit<ModalPopupProps, "isOpen" | "onClose">) => void;
  hideModal: () => void;
  modalProps: ModalPopupProps | null;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export function ModalProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const [isLoading, setIsLoading] = useState(false);
  const [modalProps, setModalProps] = useState<ModalPopupProps | null>(null);

  const showModal = (props: Omit<ModalPopupProps, "isOpen" | "onClose">) => {
    setModalProps({
      ...props,
      isOpen: true,
      onClose: () => {
        setModalProps(null);
      },
    });
  };

  const hideModal = () => {
    setModalProps(null);
  };

  useEffect(() => {
    if (pathname === "/") {
      console.log("[ModalProvider] :: useEffect :: pathname === /");
      setIsLoading(false);
    }
  }, [pathname]);

  return (
    <ModalContext.Provider value={{ showModal, hideModal, modalProps }}>
      {isLoading && <Loading />}
      {!isLoading && children}
      {modalProps && <ModalPopup {...modalProps} navigate={navigate} setIsLoading={setIsLoading} />}
    </ModalContext.Provider>
  );
}

export function useModalProvider() {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModalPopup must be used within a ModalProvider");
  }
  return context;
}

function ModalPopup(
  props: ModalPopupProps & {
    navigate: NavigateFunction;
    setIsLoading: (isLoading: boolean) => void;
  },
) {
  const { t } = useTranslation();

  if (!props.isOpen) return null;

  // 문구를 예측할 수 없는 분류는 안내 문구를 고정하고 에러 코드를 함께 노출한다.
  // - unknown : SATError.message에 영문 HTTP 문구나 i18n 키가 그대로 담길 수 있음
  // - 초기 모듈 로딩 실패 : 화면마다 다른 문구로 노출될 수 있음
  const isInitialModuleLoadFailure = props.error?.type === SATErrorType.FailToGetSecretKey;
  const isUnknown = props.error ? isUnknownError(props.error) : false;
  const fixedCopy = isInitialModuleLoadFailure
    ? {
        title: t("initialModuleLoad.failureTitle"),
        description: t("initialModuleLoad.failureDescription"),
      }
    : isUnknown
      ? { title: t("unknownError.title"), description: t("unknownError.description") }
      : undefined;

  // 문구가 정상적으로 나오는 매핑된 에러는 SATError.message 끝에 붙은 서버 원본 message만
  // 제거하고 에러 코드는 노출하지 않는다. 서버 message가 없으면 이 자리에
  // "Request failed with status code 400" 같은 개발자용 문구가 들어오기 때문이다.
  const title = fixedCopy?.title ?? props.title;
  const descriptions = fixedCopy
    ? [fixedCopy.description]
    : props.description.map((text) =>
        props.error && text === props.error.message ? removeRawMessageSuffix(text) : text,
      );
  const errorCode = props.error && fixedCopy ? getDisplayErrorCode(props.error) : undefined;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
        </div>

        <div className={styles.content}>
          <div className={styles.descriptionContainer}>
            {descriptions.map((text, index) => (
              <p key={index} className={styles.description}>
                {text}
              </p>
            ))}
            {errorCode && (
              <p className={styles.errorCode}>{t("common.errorCode", { code: errorCode })}</p>
            )}
          </div>
          {props.detail && (
            <div className={styles.detail}>
              {props.detail.split("\n").map((line, index) => (
                <div key={index} className={styles.detailItem}>
                  <span className={styles.bullet}>•</span>
                  <span>{line}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.buttonContainer}>
          <BottomButtonSection
            cancelButton={
              props.cancelButton
                ? {
                    text: props.cancelButton.title,
                    onClick: () => {
                      props.onClose();
                      props.cancelButton?.onClick();
                    },
                  }
                : undefined
            }
            submitButton={{
              text: props.okButton.title,
              onSubmit: () => {
                switch (props.error?.type) {
                  case SATErrorType.FailToGetSecretKey:
                    // 시크릿 키 초기화 실패는 라우팅만으로 복구되지 않으므로
                    // 세션 스토리지를 정리하고 전체 새로고침으로 로그인 화면부터 다시 시작한다.
                    props.setIsLoading(true);
                    console.log("[ModalPopup] :: FailToGetSecretKey || okButton :: reload `/`");
                    props.onClose();
                    sessionStorage.clear();
                    window.location.replace("/");
                    return;
                  case SATErrorType.TokenEmpty:
                  case SATErrorType.WrongToken:
                  case SATErrorType.ExpiredToken:
                  case SATErrorType.ExpiredRefreshToken:
                  case SATErrorType.ExpiredAuthToken:
                  case SATErrorType.DuplicatedLogin:
                  case SATErrorType.Unauthorized:
                  case SATErrorType.UnauthorizedRole:
                    props.setIsLoading(true);
                    console.log("[ModalPopup] :: AuthError || okButton :: replace `/`");
                    props.navigate("/", { replace: true });
                    props.onClose();
                    props.setIsLoading(false);
                    return;
                  default:
                    props.onClose();
                    props.okButton.onClick();
                    return;
                }
              },
            }}
          />
        </div>
      </div>
    </div>
  );
}
